import IEvent, { IAddAttendeeCategory, eventStatus, eventType } from "./../interfaces/IEvent";
import APIService from "./API.service";
const queryString = require("query-string");

interface IParamsGetEvents {
  archived?: boolean;
  limit?: number;
  search?: string;
  sort?: string;
  start?: number;
}

export interface IReturnGetEvents {
  data: IEvent[];
  count: number
}
interface IParamsGetEvent {
  id: number;
}

interface IParamsCreateEvent {
  name: string;
  archivingPeriod?: string;
  endDateTime?: string;
  eventType?: string;
  group?: string;
  startDateTime?: string;
  timeZone?: string;
  transformationsEnabled?: boolean;
  city?: string;
  locationName?: string;
  type?: eventType;
  status?: eventStatus;
  logoUrl?: string;
  singleAttendeeSyncUrl?: string;
  autoSwitchSession?: boolean
  hasFraudDetection?:boolean
}

interface IParamsUpdateEvent extends IParamsCreateEvent {
  id: number;
}

class EventService extends APIService {
  // Get events by filter
  getEvents(params: IParamsGetEvents): Promise<IReturnGetEvents> {
    const query = {
      token: localStorage.getItem("fielddrive-token"),
      archived: params.archived || false,
      ...(params.limit && { limit: params.limit }),
      search: params.search || "",
      sort:
        params.sort ||
        JSON.stringify([{ direction: "desc", property: "startDateTime" }]),
      start: params.start || 0,
      limit: params.limit || 50
    };
    return this.call.get(`${this.apiUrl}/events?${queryString.stringify(query)}`) as Promise<IReturnGetEvents>;
  }

  // Get an event
  getEvent(params: IParamsGetEvent) {
    const query = {
      token: localStorage.getItem("fielddrive-token"),
    };
    return this.call.get(
      `${this.apiUrl}/events/${params.id}?${queryString.stringify(query)}`
    );
  }

  // Create a new event
  createEvent(params: IParamsCreateEvent) {
    const query = {
      token: localStorage.getItem("fielddrive-token"),
    };
    const postData = {
      name: params.name,
      eventType: params.eventType || "generic",
      archivingPeriod: params.archivingPeriod ||"14",
      transformationsEnabled: params.transformationsEnabled || false,
      type: params.type || eventType.ONLINE,
      status: params.status || eventStatus.PRE_EVENT,
      ...(params.endDateTime && { endDateTime: params.endDateTime }),
      ...(params.group && { group: params.group }),
      ...(params.startDateTime && { startDateTime: params.startDateTime }),
      ...(params.timeZone && { timeZone: params.timeZone }),
      ...(params.logoUrl && { logoUrl: params.logoUrl }),
      ...(params.city && { city: params.city }),
      ...(params.locationName && { locationName: params.locationName }),
      ...(params.hasFraudDetection && { hasFraudDetection: params.hasFraudDetection }),
    };
    return this.call.post(
      `${this.apiUrl}/events?${queryString.stringify(query)}`,
      JSON.stringify(postData)
    );
  }

  // Update an event
  updateEvent(params: IParamsUpdateEvent) {
    const query = {
      token: localStorage.getItem("fielddrive-token"),
    };
    const postData = {
      name: params.name,
      eventType: params.eventType || "generic",
      archivingPeriod: params.archivingPeriod || "14",
      transformationsEnabled: params.transformationsEnabled || false,
      type: params.type || eventType.ONLINE,
      status: params.status || eventStatus.PRE_EVENT,
      ...(params.endDateTime && { endDateTime: params.endDateTime }),
      ...(params.group && { group: params.group }),
      ...(params.startDateTime && { startDateTime: params.startDateTime }),
      ...(params.timeZone && { timeZone: params.timeZone }),
      ...(params.logoUrl && { logoUrl: params.logoUrl }),
      ...(params.city && { city: params.city }),
      ...(params.locationName && { locationName: params.locationName }),
      ...(params.hasFraudDetection && { hasFraudDetection: params.hasFraudDetection }),
      singleAttendeeSyncUrl: params?.singleAttendeeSyncUrl || "",
      autoSwitchSession: params?.autoSwitchSession || false
    };
    return this.call.put(
      `${this.apiUrl}/events/${params.id}?${queryString.stringify(query)}`,
      JSON.stringify(postData)
    );
  }

  // Get event job object
  getEventJob(eventId: number, jobId: number) {
    const query = {
      token: localStorage.getItem("fielddrive-token"),
    };
    return this.call.get(
      `${this.apiUrl}/events/${eventId}/job/${jobId}?${queryString.stringify(query)}`
    );
  }

  // Process CSV file
  // processCSVFile(eventId: number, filePath: string) {
  //   const token = localStorage.getItem("fielddrive-token");
  //   const body = {
  //     fieldsMatch: csvFieldsMatch,
  //     filePath,
  //   };
  //   return this.call.post(
  //     `${this.apiUrl}/events/${eventId}/processCSV?token=${token}`,
  //     body
  //   );
  // }

  // Delete an event
  delete(eventId: number) {
    const token = localStorage.getItem("fielddrive-token");
    return this.call.delete(`${this.apiUrl}/events/${eventId}?token=${token}`);
  }

  // Set/Unset Archive flag
  setArchive(eventId: number, enable: boolean) {
    const token = localStorage.getItem("fielddrive-token");
    return this.call.post(
      `${this.apiUrl}/events/${eventId}/setArchive?enable=${enable}&token=${token}`,
      {}
    );
  }

  // Get the attendee categories for a specific event
  getAttendeeCategories(eventId: number) {
    const token = localStorage.getItem("fielddrive-token");
    return this.call.get(
      `${this.apiUrl}/events/${eventId}/attendeeCategories?token=${token}`
    );
  }

  // Get the attendee categories for a specific event
  getAttendeeCountedCategories<T>(eventId: number): T {
    const token = localStorage.getItem("fielddrive-token");
    return (this.call.get(
      `${this.apiUrl}/events/${eventId}/countedAttendeesByCategories?token=${token}`
    ) as T);
  }


  // Add the attendee categories for a specific event
  addAttendeeCategory<T>(eventId: number, payload: IAddAttendeeCategory): T {
    const token = localStorage.getItem("fielddrive-token");
    return (this.call.post(
      `${this.apiUrl}/events/${eventId}/attendeeCategories?token=${token}`,
      payload
    ) as T);
  }

  // Update the attendee categories for a specific event
  modifyAttendeeCategory<T>(eventId: number, id: string, categoryName: string): T {
    const token = localStorage.getItem("fielddrive-token");
    return (this.call.put(
      `${this.apiUrl}/events/${eventId}/attendeeCategories/${id}?token=${token}`,
      { name: categoryName }
    ) as T);
  }

  // Delete the attendee categories for a specific event
  deleteAttendeeCategory<T>(eventId: number, id: string): T {
    const token = localStorage.getItem("fielddrive-token");
    return (this.call.delete(
      `${this.apiUrl}/events/${eventId}/attendeeCategories/${id}?token=${token}`
    ) as T);
  }

  // Get the attendee fields for a specific event
  getAttendeeFields(eventId: number, params?: any) {
    const query = {
      id: eventId,
      token: localStorage.getItem("fielddrive-token"),
    };

    return this.call.get(
      `${this.apiUrl}/events/${eventId}/attendeeFields?${queryString.stringify(
        query
      )}`
    );
  }

  // Reset all event data
  reset(eventId: number) {
    const token = localStorage.getItem("fielddrive-token");
    return this.call.post(
      `${this.apiUrl}/events/${eventId}/reset?token=${token}`,
      {}
    );
  }

  // Get the Excel report
  getExcelReportUrl(eventId: number) {
    const token = localStorage.getItem("fielddrive-token");
    // return this.call.get(
    return `${this.apiUrl}/api/v1/report/events/${eventId}/excel/complete?token=${token}`;
    // {}
    // );
  }

  // Get the attendee records only
  getAttendeesReportUrl(eventId: number) {
    const token = localStorage.getItem("fielddrive-token");
    // return this.call.get(
    return `${this.apiUrl}/events/${eventId}/csvexport?token=${token}`;
    // {}
    // );
  }

  // Get the session scans only
  getSessionScansReportUrl(eventId: number) {
    const token = localStorage.getItem("fielddrive-token");
    // return this.call.get(
    return `${this.apiUrl}/api/v1/report/events/${eventId}/attendeeScans?token=${token}`;
    // {}
    // );
  }

  // Get the check-ins only
  getCheckHistoriesReportUrl(eventId: number) {
    const token = localStorage.getItem("fielddrive-token");
    // return this.call.get(
    return `${this.apiUrl}/api/v1/report/events/${eventId}/attendeeCheckHistories?token=${token}`;
    // {}
    // );
  }
}

export default EventService;
